import type { Application, Party } from '@ps/api-client/staff';
import { Pill } from '@ps/ui-kit/components/pill';

// A-02 party graph (prototype layout): the applicant first, then who holds what in it, one party per row with a dashed
// divider: "↳ Name [Hit]" and, below, the roles ("Director · UBO 55%") and the identity-check status. A possible
// screening match shows a red "Hit" pill until it is dispositioned.

const CHECK: Record<string, string> = {
  VERIFIED: 'verified', PENDING: 'check pending', MANUAL_REVIEW: 'manual review', FAILED: 'check failed', PENDING_RFI: 'waiting for the applicant', EXPIRED: 'check expired', CANCELLED: 'check cancelled by the applicant',
};

export function PartyGraph({ app, uboIds }: { app: Application; uboIds: string[] }) {
  const root = app.parties.find(p => p.isApplicant);
  if (!root) return null;
  const rows: Array<{ party: Party; depth: number; roles: string[] }> = [];
  const walk = (party: Party, depth: number, roles: string[], seen: Set<string>) => {
    rows.push({ party, depth, roles });
    const byHolder = new Map<string, string[]>();
    for (const r of app.relationships.filter(x => x.toPartyId === party.id)) {
      byHolder.set(r.fromPartyId, [...(byHolder.get(r.fromPartyId) ?? []), r.type === 'OWNS' ? `${r.ownershipPct}%` : r.type === 'DIRECTOR_OF' ? 'Director' : r.type === 'SIGNATORY_OF' ? 'Authorised signatory' : 'Controls']);
    }
    for (const [id, r] of byHolder) {
      const holder = app.parties.find(p => p.id === id);
      if (holder && !seen.has(id)) walk(holder, depth + 1, r, new Set(seen).add(id));
    }
  };
  walk(root, 0, [], new Set([root.id]));
  return (
    <ul aria-label="Ownership and control" className="divide-y divide-dashed divide-line">
      {rows.map(({ party, depth, roles }) => {
        const openHits = app.hits.filter(h => h.partyId === party.id && !h.disposition).length;
        const ubo = uboIds.includes(party.id);
        const owns = roles.find(r => r.endsWith('%'));
        const what = [...roles.filter(r => !r.endsWith('%')), ...(owns ? [ubo ? `UBO ${owns}` : `Shareholder ${owns}`] : ubo ? ['UBO'] : [])];
        const sub = depth === 0
          ? `${party.kind === 'ENTITY' ? 'Entity · KYB' : 'Individual · identity'} ${CHECK[party.idVerification] ?? party.idVerification.toLowerCase()}`
          : [...what, `ID ${CHECK[party.idVerification] ?? party.idVerification.toLowerCase()}`].join(' · ');
        return (
          <li key={party.id} className="py-2.5" style={{ paddingLeft: Math.min(depth, 3) * 14 }}>
            <p className="text-sm text-ink">
              {depth > 0 && <span aria-hidden className="mr-1 text-ink-3">↳</span>}
              <span className={depth === 0 ? 'font-semibold' : undefined}>{party.name}</span>
              {openHits > 0 && <Pill tone="bad" className="ml-1.5">Hit</Pill>}
              {!openHits && party.idVerification === 'MANUAL_REVIEW' && <Pill tone="warn" className="ml-1.5">Review</Pill>}
            </p>
            <p className="text-[13px] text-ink-3">{sub}</p>
          </li>
        );
      })}
    </ul>
  );
}
